/**
 * Quote-part Cash events display expansion (display-only).
 *
 * The monthly ledger already splits each buyer transaction. Gross sale events
 * from buildCashEvents still say buyer → SPV for the full amount — false under
 * Quote-part. After the ledger runs, replace each sale with:
 *   - optional non-cash transaction-total context (never buyer → SPV)
 *   - SPV cash (proceeds + reimbursement + land excess that enters SPV)
 *   - land settlement paid directly buyer → bank
 *   - buyer construction paid directly buyer → contractor
 *
 * Split cash amounts conserve to the gross sale. Does not change ledger / P&L.
 */

import { MODES } from './defaults.js';
import { allocateExact, n } from './math.js';

export const QP_SALE_KINDS = {
  context: 'sale',
  spvCash: 'sale_spv_cash',
  land: 'sale_land_settlement',
  construction: 'sale_buyer_construction',
};

export function isQuotePartSaleCashEvent(ev) {
  return (
    ev
    && (ev.kind === QP_SALE_KINDS.spvCash
      || ev.kind === QP_SALE_KINDS.land
      || ev.kind === QP_SALE_KINDS.construction)
  );
}

export function isQuotePartSaleContextEvent(ev) {
  return Boolean(ev && ev.kind === QP_SALE_KINDS.context && ev.meta?.nonCash);
}

/**
 * Cash that actually enters the SPV from a Quote-part buyer transaction row.
 */
export function spvBuyerCashFromLedgerRow(row) {
  if (!row?.buyerTransaction) return 0;
  const t = row.buyerTransaction;
  return (
    Math.max(0, n(t.usableSpvProceeds))
    + Math.max(0, n(t.constructionReimbursement))
    + Math.max(0, n(row.landSettlementToSpvCash))
  );
}

/**
 * Expand Quote-part gross sale events into conserved party-routed cash rows.
 * No-op for other modes or incomplete ledgers.
 *
 * @param {object[]} cashEvents timing SoT events (pre-expansion)
 * @param {object} ledger buildLedger result
 * @returns {object[]} display cash events
 */
export function expandQuotePartCashEvents(cashEvents, ledger) {
  if (!ledger || ledger.incomplete || ledger.mode !== MODES.quotePart) {
    return cashEvents || [];
  }

  const events = cashEvents || [];
  const rowByMonth = new Map((ledger.months || []).map((m) => [m.month, m]));

  const saleEvents = events.filter((e) => e.kind === 'sale' && !e.meta?.nonCash);
  if (!saleEvents.length) return events;

  const salesByMonth = new Map();
  for (const sale of saleEvents) {
    const list = salesByMonth.get(sale.month) || [];
    list.push(sale);
    salesByMonth.set(sale.month, list);
  }

  /** @type {Map<string, { spvCash: number, landBank: number, buyerBuild: number, gross: number }>} */
  const splitsBySaleId = new Map();

  for (const [month, monthSales] of salesByMonth) {
    const row = rowByMonth.get(month);
    const txn = row?.buyerTransaction;
    const weights = monthSales.map((s) => Math.max(0, n(s.amount)));
    const weightSum = weights.reduce((s, w) => s + w, 0);

    if (!txn || weightSum <= 1e-12) {
      // No ledger split — keep gross as non-cash context only (never buyer → SPV).
      for (const sale of monthSales) {
        splitsBySaleId.set(sale.id, {
          spvCash: 0,
          landBank: 0,
          buyerBuild: 0,
          gross: n(sale.amount),
        });
      }
      continue;
    }

    const spvTotal = spvBuyerCashFromLedgerRow(row);
    const landTotal = Math.max(0, n(row.buyerDirectLandSettlement));
    const spvParts = allocateExact(spvTotal, weights);
    const landParts = allocateExact(landTotal, weights);

    monthSales.forEach((sale, i) => {
      const gross = n(sale.amount);
      const spvCash = Math.max(0, spvParts[i] || 0);
      const landBank = Math.max(0, landParts[i] || 0);
      // Last leg absorbs float so each sale's cash legs conserve to its gross.
      const buyerBuild = gross - spvCash - landBank;
      splitsBySaleId.set(sale.id, {
        spvCash,
        landBank,
        buyerBuild,
        gross,
      });
    });
  }

  const out = [];
  for (const ev of events) {
    if (ev.kind !== 'sale' || ev.meta?.nonCash) {
      out.push(ev);
      continue;
    }

    const split = splitsBySaleId.get(ev.id) || {
      spvCash: 0,
      landBank: 0,
      buyerBuild: 0,
      gross: n(ev.amount),
    };

    // Non-cash context: same kind 'sale' so timing rematerialization stays valid
    // if these display events are reused — never labelled buyer → SPV.
    out.push({
      ...ev,
      id: `${ev.id}-total`,
      label: `${ev.label} (transaction total — non-cash)`,
      direction: 'in',
      payer: 'buyer',
      recipient: 'transaction_total',
      meta: {
        ...(ev.meta || {}),
        nonCash: true,
        role: 'transaction_total',
        parentSaleId: ev.id,
        originalRecipient: ev.recipient,
      },
    });

    if (Math.abs(split.spvCash) > 1e-12) {
      out.push({
        id: `${ev.id}-spv-cash`,
        month: ev.month,
        kind: QP_SALE_KINDS.spvCash,
        label: `${ev.label} — SPV cash (proceeds / reimbursement)`,
        amount: split.spvCash,
        direction: 'in',
        payer: 'buyer',
        recipient: 'spv',
        source: ev.source,
        recurring: false,
        category: null,
        meta: {
          parentSaleId: ev.id,
          role: 'spv_cash',
          phase: ev.meta?.phase || null,
          cash: true,
        },
      });
    }

    if (Math.abs(split.landBank) > 1e-12) {
      out.push({
        id: `${ev.id}-land`,
        month: ev.month,
        kind: QP_SALE_KINDS.land,
        label: `${ev.label} — land settlement (direct)`,
        amount: split.landBank,
        direction: 'in',
        payer: 'buyer',
        recipient: 'bank',
        source: ev.source,
        recurring: false,
        category: null,
        meta: {
          parentSaleId: ev.id,
          role: 'land_settlement',
          phase: ev.meta?.phase || null,
          cash: true,
        },
      });
    }

    if (Math.abs(split.buyerBuild) > 1e-12) {
      out.push({
        id: `${ev.id}-construction`,
        month: ev.month,
        kind: QP_SALE_KINDS.construction,
        label: `${ev.label} — buyer construction (direct)`,
        amount: split.buyerBuild,
        direction: 'in',
        payer: 'buyer',
        recipient: 'contractor',
        source: ev.source,
        recurring: false,
        category: null,
        meta: {
          parentSaleId: ev.id,
          role: 'buyer_construction',
          phase: ev.meta?.phase || null,
          cash: true,
        },
      });
    }
  }

  return out;
}

/**
 * For tests: per parent sale, cash legs sum to gross and SPV cash matches role.
 */
export function quotePartSaleSplitSummary(cashEvents) {
  const byParent = new Map();
  for (const ev of cashEvents || []) {
    if (isQuotePartSaleContextEvent(ev)) {
      const id = ev.meta.parentSaleId || ev.id;
      const row = byParent.get(id) || { gross: 0, spvCash: 0, land: 0, construction: 0 };
      row.gross = n(ev.amount);
      byParent.set(id, row);
      continue;
    }
    if (!isQuotePartSaleCashEvent(ev)) continue;
    const id = ev.meta?.parentSaleId;
    if (!id) continue;
    const row = byParent.get(id) || { gross: 0, spvCash: 0, land: 0, construction: 0 };
    if (ev.kind === QP_SALE_KINDS.spvCash) row.spvCash += n(ev.amount);
    if (ev.kind === QP_SALE_KINDS.land) row.land += n(ev.amount);
    if (ev.kind === QP_SALE_KINDS.construction) row.construction += n(ev.amount);
    byParent.set(id, row);
  }
  return [...byParent.entries()].map(([parentSaleId, parts]) => ({
    parentSaleId,
    ...parts,
    cashTotal: parts.spvCash + parts.land + parts.construction,
    conserves: Math.abs(parts.spvCash + parts.land + parts.construction - parts.gross) <= 0.05,
  }));
}
